// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/nodes/MissedNode.js (392 lines).
 * BYTE-IDENTICAL — reactflow → @xyflow/react, AudioPreviewModel → AudioPreviewModal.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Handle, useReactFlow } from '@xyflow/react';
import { Button, Form } from './_rbShim';
import { NodesContext } from '../provider/IvrContext';
import { toast } from 'sonner';
import { PhoneMissed, Settings, Trash2 } from 'lucide-react';
import IvrDrawer from '../Helpers/IvrDrawer';
import AudioPreviewModal from '../Helpers/AudioPreviewModal';
import { triggerHandleConnect, findUpstreamLanguageNode } from '../Helpers/utils';
import {
  BACKEND_API_BASE_URL,
  VOICEMAIL_MAX_DURATION,
  MISSED_NODE_OPTIONS,
} from '@/utilities/constant';
import { getApiCall } from '@/service/httpCall';
import { API_WEBHOOK_LIST, API_SMSWEBHOOK_LIST } from '@/service/url';

const MissedNode = ({ data, id }: any) => {
  const { updateNodeData, onRemove } = useContext(NodesContext) as any;
  const { getNodes, getEdges } = useReactFlow();

  const mode = data.mode || '';
  const action = data.action || {};
  const maxDuration = data.max_duration || '';

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [promptList, setPromptList] = useState<any[]>([]);
  const [webhookList, setWebhookList] = useState<any[]>([]);
  const [smsWebhookList, setSmsWebhookList] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});

  const [answerLangCodes, setAnswerLangCodes] = useState<any[]>([]);
  const [hasLangParent, setHasLangParent] = useState(false);
  const [draftMode, setDraftMode] = useState(mode);
  const [draftAction, setDraftAction] = useState<any>(action);
  const [draftLanguage, setDraftLanguage] = useState(action.language || '');
  const [draftCategory, setDraftCategory] = useState('general');
  const [draftDuration, setDraftDuration] = useState(maxDuration);
  const [draftWebhookId, setDraftWebhookId] = useState(
    action?.webhook_id || '',
  );
  const [draftSmsWebhookId, setDraftSmsWebhookId] = useState(
    action?.sms_webhook_id || '',
  );
  const [drawerPreview, setDrawerPreview] = useState<any>({
    open: false,
    url: '',
    name: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        if (data.userId) {
          const resp: any = await getApiCall(
            `${BACKEND_API_BASE_URL}/v2/prompt?user_id=${data.userId}`,
          );
          if (Array.isArray(resp)) {
            setPromptList(
              resp.filter(
                (p: any) =>
                  p.prompt_category === 'general' &&
                  Array.isArray(p.prompt_url) &&
                  p.prompt_url.some((e: any) => e.status === 1),
              ),
            );
          }
        }
        getApiCall(API_WEBHOOK_LIST)
          .then((r: any) => setWebhookList(r || []))
          .catch(console.log);
        getApiCall(API_SMSWEBHOOK_LIST)
          .then((r: any) => setSmsWebhookList(r || []))
          .catch(console.log);
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [data.userId]);

  const updateNode = (updates: any) => updateNodeData(id, updates);

  const openModal = () => {
    const _answerCodes =
      getNodes().find((n: any) => n.type === 'answerNode')?.data?.languages ||
      [];
    const _langNode = findUpstreamLanguageNode(id, getNodes, getEdges);
    const _langCodes =
      _langNode &&
      Array.isArray(_langNode.data?.selectedLanguages) &&
      _langNode.data.selectedLanguages.length > 0
        ? _langNode.data.selectedLanguages
        : null;
    setHasLangParent(!!_langNode);
    setAnswerLangCodes(_langCodes ?? _answerCodes);
    setDraftMode(mode);
    setDraftAction(action);
    setDraftLanguage(action.language || '');
    setDraftCategory('general');
    setDraftDuration(maxDuration);
    setDraftWebhookId(action?.webhook_id || '');
    setDraftSmsWebhookId(action?.sms_webhook_id || '');
    setErrors({});
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setErrors({});
    setModalIsOpen(false);
  };

  const handleModeChange = (e: any) => {
    const m = e.target.value;
    setDraftMode(m);
    setDraftAction({});
    setDraftCategory('');
    setDraftDuration('');
    setDraftWebhookId('');
    setDraftSmsWebhookId('');
  };

  const handleAudioChange = (e: any) => {
    const selected = promptList.find(
      (a: any) => a.id.toString() === e.target.value,
    );
    if (!selected) {
      setDraftAction((prev: any) => ({
        ...prev,
        id: '',
        audio: '',
        name: '',
        duration: 0,
      }));
      setDraftLanguage('');
      return;
    }
    const approvedLangs = Array.isArray(selected.prompt_url)
      ? selected.prompt_url.filter(
          (en: any) =>
            en.status === 1 &&
            (answerLangCodes.length === 0 ||
              answerLangCodes.includes(en.language)),
        )
      : [];

    if (hasLangParent) {
      // Downstream of a Language node — keep every approved language variant, no manual pick.
      setDraftAction({
        type: draftMode,
        prompt_category: 'general',
        id: selected.id,
        name: selected.prompt_name,
        duration: selected.prompt_duration || 0,
        languages: approvedLangs
          .filter((en: any) => en.status === 1)
          .map((en: any) => ({
            language: en.language,
            url: en.url,
            duration: en.duration ?? selected.prompt_duration ?? 0,
            status: en.status,
          })),
      });
      setDraftLanguage('');
      return;
    }

    const autoLang = approvedLangs.length === 1 ? approvedLangs[0].language : '';
    const autoAudio = approvedLangs.length === 1 ? approvedLangs[0].url : '';
    setDraftAction({
      type: draftMode,
      prompt_category: 'general',
      id: selected.id,
      audio: autoAudio,
      duration: selected.prompt_duration || 0,
      name: selected.prompt_name,
      language: autoLang,
    });
    setDraftLanguage(autoLang);
  };

  const handleLanguageChange = (e: any) => {
    const lang = e.target.value;
    setDraftLanguage(lang);
    const sel = promptList.find(
      (a: any) => a.id?.toString() === draftAction?.id?.toString(),
    );
    const entry =
      lang && sel && Array.isArray(sel.prompt_url)
        ? sel.prompt_url.find((en: any) => en.language === lang)
        : null;
    setDraftAction((prev: any) => ({
      ...prev,
      audio: entry?.url || '',
      language: lang,
    }));
  };

  const handleDurationChange = (e: any) => {
    const val = e.target.value;
    if (
      /^\d*$/.test(val) &&
      (val === '' || Number(val) <= VOICEMAIL_MAX_DURATION)
    ) {
      setDraftDuration(val);
    }
  };

  const handleWebhookChange = (e: any) => {
    const wid = e.target.value;
    setDraftWebhookId(wid);
    const selected = webhookList.find((w: any) => w.id.toString() === wid);
    setDraftAction({
      type: 'webhook',
      webhook_id: selected?.id || '',
      name: selected?.webhook_name || selected?.name || '',
      url: selected?.url || '',
    });
  };

  const handleSmsWebhookChange = (e: any) => {
    const wid = e.target.value;
    setDraftSmsWebhookId(wid);
    const selected = smsWebhookList.find((w: any) => w.id.toString() === wid);
    setDraftAction({
      type: 'sms_webhook',
      sms_webhook_id: selected?.id || '',
      name: selected?.webhook_name || '',
      url: selected?.url || '',
      payload: selected?.payload || '',
      request_type: selected?.request_type || '',
      sms_text: selected?.sms_text || '',
      header: selected?.header || '',
    });
  };

  const validate = () => {
    const newErrors: any = {};
    if (!draftMode) newErrors.mode = 'Mode is required';

    if (draftMode === 'hangup_with_media' || draftMode === 'voicemail') {
      if (draftCategory && !draftAction?.id) {
        newErrors.audio = 'Audio is required when category is selected';
      }
      if (!hasLangParent && draftAction?.id && !draftLanguage)
        newErrors.language = 'Please select a language';
    }
    if (draftMode === 'voicemail') {
      if (!draftDuration || Number(draftDuration) < 1)
        newErrors.duration = 'Duration required';
      else if (Number(draftDuration) > VOICEMAIL_MAX_DURATION)
        newErrors.duration = `Max ${VOICEMAIL_MAX_DURATION}s`;
    }
    if (draftMode === 'webhook' && !draftWebhookId)
      newErrors.webhook = 'Webhook required';
    if (draftMode === 'sms_webhook' && !draftSmsWebhookId)
      newErrors.smsWebhook = 'SMS Webhook required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const updates: any = { mode: draftMode };
    if (draftMode === 'hangup') {
      updates.action = {};
      updates.max_duration = null;
    } else if (draftMode === 'webhook' || draftMode === 'sms_webhook') {
      updates.action = draftAction;
      updates.max_duration = null;
    } else {
      updates.action = draftAction;
      updates.max_duration =
        draftMode === 'voicemail' ? Number(draftDuration) : null;
    }
    updateNode(updates);
    toast.success('Missed action updated.');
    setModalIsOpen(false);
  };

  const displayPromptList =
    answerLangCodes.length === 0
      ? promptList
      : promptList.filter(
          (p: any) =>
            Array.isArray(p.prompt_url) &&
            p.prompt_url.some(
              (e: any) =>
                e.status === 1 && answerLangCodes.includes(e.language),
            ),
        );

  const modeLabel =
    MISSED_NODE_OPTIONS.find((o: any) => o.value === mode)?.label || 'Missed';

  const renderListenBtn = (url: any, name: any) =>
    url ? (
      <button
        type="button"
        className="ivr-drawer-play-btn"
        title="Listen"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() =>
          setDrawerPreview({ open: true, url, name: name || 'Audio' })
        }
        style={{ marginLeft: 8 }}
      >
        ▶
      </button>
    ) : null;

  return (
    <div className="ivrn" style={{ '--node-color': '#9333ea' } as any}>
      <span
        className="ivrn-icon"
        style={{ background: '#9333ea', color: '#fff' }}
        onClick={triggerHandleConnect}
      >
        <PhoneMissed size={22} strokeWidth={2} />
      </span>
      <Handle
        type="target"
        position={'left' as any}
        id="missed-target"
        style={{ background: '#9333ea', top: '65%' }}
      />

      <div className="ivrn-head">
        <span className="ivrn-title">Missed Call</span>
      </div>
      <div className="ivrn-body">
        {mode ? (
          <span className="ivrn-val">{modeLabel}</span>
        ) : (
          <span className="ivrn-hint">Click ⚙ to set action</span>
        )}
      </div>

      <div className="ivrn-actions">
        <button
          className="ivrn-btn ivrn-btn--cfg"
          title="Configure"
          onClick={openModal}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
        <button
          className="ivrn-btn ivrn-btn--del"
          title="Remove"
          onClick={() => onRemove(id)}
        >
          <Trash2 size={14} strokeWidth={2} />
        </button>
      </div>

      <IvrDrawer
        show={modalIsOpen}
        onHide={closeModal}
        title="Missed Call Action"
        subtitle="Configure the action taken when a call is missed."
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Close
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </>
        }
      >
        <Form>
          <Form.Group>
            <Form.Label>
              Action<Form.Text className="text-danger">*</Form.Text>
            </Form.Label>
            <Form.Control
              as="select"
              value={draftMode}
              onChange={handleModeChange}
            >
              <option value="">Select Action</option>
              {MISSED_NODE_OPTIONS.map((m: any) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Form.Control>
            {errors.mode && (
              <Form.Text className="text-danger">{errors.mode}</Form.Text>
            )}
          </Form.Group>

          {(draftMode === 'hangup_with_media' ||
            draftMode === 'voicemail') && (
            <>
              {draftMode === 'hangup_with_media' && (
                <Form.Text className="text-warning">
                  Note: Ring Duration will be limited to 2 seconds.
                </Form.Text>
              )}
              <Form.Group style={{ marginTop: 15 }}>
                <Form.Label>Select Audio</Form.Label>
                <Form.Control
                  as="select"
                  value={draftAction?.id || ''}
                  onChange={handleAudioChange}
                >
                  <option value="">Select Audio</option>
                  {displayPromptList.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.prompt_name}
                    </option>
                  ))}
                </Form.Control>
                {errors.audio && (
                  <Form.Text className="text-danger">{errors.audio}</Form.Text>
                )}
                {renderListenBtn(
                  draftAction?.audio ||
                    draftAction?.languages?.[0]?.url ||
                    '',
                  draftAction?.name,
                )}
              </Form.Group>
              {hasLangParent ? (
                Array.isArray(draftAction?.languages) &&
                draftAction.languages.length > 0 && (
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 12,
                      color: '#0f766e',
                      background: '#f0fdfa',
                      border: '1px solid #99f6e4',
                      borderRadius: 8,
                      padding: '8px 12px',
                    }}
                  >
                    This node is downstream of a Language node — all{' '}
                    {draftAction.languages.length} approved language
                    {draftAction.languages.length !== 1 ? 's' : ''} (
                    {draftAction.languages
                      .map((l: any) => l.language?.toUpperCase())
                      .join(', ')}
                    ) will be saved automatically.
                  </div>
                )
              ) : (
                (() => {
                  const sel = promptList.find(
                    (a: any) =>
                      a.id?.toString() === draftAction?.id?.toString(),
                  );
                  const langs = Array.isArray(sel?.prompt_url)
                    ? sel.prompt_url.filter(
                        (e: any) =>
                          e.status === 1 &&
                          (answerLangCodes.length === 0 ||
                            answerLangCodes.includes(e.language)),
                      )
                    : [];
                  if (!sel || langs.length === 0) return null;
                  return (
                    <Form.Group style={{ marginTop: 10 }}>
                      <Form.Label>
                        Select Audio Language{' '}
                        <span style={{ color: 'red' }}>*</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        value={draftLanguage}
                        onChange={handleLanguageChange}
                      >
                        <option value="">Select Language</option>
                        {langs.map((e: any) => (
                          <option key={e.language} value={e.language}>
                            {e.language?.toUpperCase()}
                            {e.name ? ` — ${e.name}` : ''}
                          </option>
                        ))}
                      </Form.Control>
                      {errors.language && (
                        <Form.Text className="text-danger">
                          {errors.language}
                        </Form.Text>
                      )}
                    </Form.Group>
                  );
                })()
              )}
            </>
          )}

          {draftMode === 'voicemail' && (
            <Form.Group style={{ marginTop: 15 }}>
              <Form.Label>
                Max Voicemail Duration (sec, max {VOICEMAIL_MAX_DURATION})
                <Form.Text className="text-danger">*</Form.Text>
              </Form.Label>
              <Form.Control
                type="number"
                min="1"
                max={VOICEMAIL_MAX_DURATION}
                value={draftDuration}
                onChange={handleDurationChange}
              />
              {errors.duration && (
                <Form.Text className="text-danger">
                  {errors.duration}
                </Form.Text>
              )}
            </Form.Group>
          )}

          {draftMode === 'webhook' && (
            <Form.Group style={{ marginTop: 15 }}>
              <Form.Label>
                Select Webhook<Form.Text className="text-danger">*</Form.Text>
              </Form.Label>
              <Form.Control
                as="select"
                value={draftWebhookId}
                onChange={handleWebhookChange}
              >
                <option value="">Select Webhook</option>
                {webhookList.map((w: any) => (
                  <option key={w.id} value={w.id}>
                    {w.webhook_name || w.name}
                  </option>
                ))}
              </Form.Control>
              {errors.webhook && (
                <Form.Text className="text-danger">{errors.webhook}</Form.Text>
              )}
            </Form.Group>
          )}

          {draftMode === 'sms_webhook' && (
            <Form.Group style={{ marginTop: 15 }}>
              <Form.Label>
                Select SMS Webhook
                <Form.Text className="text-danger">*</Form.Text>
              </Form.Label>
              <Form.Control
                as="select"
                value={draftSmsWebhookId}
                onChange={handleSmsWebhookChange}
              >
                <option value="">Select SMS Webhook</option>
                {smsWebhookList.map((w: any) => (
                  <option key={w.id} value={w.id}>
                    {w.webhook_name}
                  </option>
                ))}
              </Form.Control>
              {errors.smsWebhook && (
                <Form.Text className="text-danger">
                  {errors.smsWebhook}
                </Form.Text>
              )}
            </Form.Group>
          )}
        </Form>
      </IvrDrawer>

      <AudioPreviewModal
        show={drawerPreview.open}
        onClose={() =>
          setDrawerPreview({ open: false, url: '', name: '' })
        }
        audioUrl={drawerPreview.url}
        title={drawerPreview.name}
      />
    </div>
  );
};

export default MissedNode;
